import { useEffect, useState } from "react";

const CONSENT_KEY = "cookie-consent";

type ConsentValue = "accepted" | "declined" | null;

const translations = {
  en: {
    message: "We use functional cookies to keep the site working. No tracking cookies without your consent.",
    accept: "Accept",
    decline: "Decline",
    label: "Cookie notice",
  },
  nl: {
    message: "Wij gebruiken functionele cookies om de site werkende te houden. Geen tracking cookies zonder jouw toestemming.",
    accept: "Accepteren",
    decline: "Afwijzen",
    label: "Cookiemelding",
  },
};

function getBrowserLanguage(): "en" | "nl" {
  if (typeof navigator === "undefined") return "en";
  const lang = navigator.language || (navigator as unknown as { userLanguage?: string }).userLanguage || "en";
  return lang.toLowerCase().startsWith("nl") ? "nl" : "en";
}

const CookieBanner = () => {
  const [mounted, setMounted] = useState(false);
  const [consent, setConsent] = useState<ConsentValue>(null);
  const lang = getBrowserLanguage();
  const t = translations[lang];

  useEffect(() => {
    setMounted(true);
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (stored === "accepted" || stored === "declined") {
        setConsent(stored);
      }
    } catch {
      // Ignore localStorage access errors
    }
  }, []);

  const handleChoice = (choice: ConsentValue) => {
    setConsent(choice);
    try {
      localStorage.setItem(CONSENT_KEY, choice ?? "declined");
    } catch {
      // ignore
    }
  };

  if (!mounted || consent !== null) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t.label}
      className="fixed bottom-20 left-0 right-0 z-[60] border-t border-[var(--color-primary)]/20 bg-[var(--color-card)]/95 p-4 text-[var(--color-text)] shadow-lg backdrop-blur-sm md:bottom-0"
    >
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 md:flex-row">
        <p className="text-center text-sm md:text-left">{t.message}</p>
        <div className="flex shrink-0 items-center gap-3">
          <button
            onClick={() => handleChoice("declined")}
            className="rounded-md px-4 py-2 text-sm font-medium text-[var(--color-text)]/70 transition-colors hover:text-[var(--color-text)]"
          >
            {t.decline}
          </button>
          <button
            onClick={() => handleChoice("accepted")}
            className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-button-text)] transition-colors hover:opacity-90"
          >
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
